// src/utils/bozzeLocali.js
// ═══════════════════════════════════════════════════════════════════════════
// BOZZE AUTOMATICHE DEI MODULI LUNGHI (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Sul telefono, passando a un'altra app (telefonata, WhatsApp, fotocamera), il sistema
// può chiudere la pagina: un rapportino compilato a metà andava perso.
// Qui, mentre si compila, il modulo si salva da solo SUL DISPOSITIVO (non nel database):
//  • dopo ogni modifica (con un attimo di pausa) e subito quando si esce dall'app;
//  • solo se si è davvero scritto qualcosa (un modulo aperto e lasciato vuoto non crea bozze);
//  • foto e firme comprese: si usa l'archivio del browser adatto ai dati pesanti (IndexedDB);
//  • la bozza è dell'utente che la scrive e di quel dispositivo; si cancella quando il
//    modulo viene salvato o quando si sceglie «Elimina bozza»; dopo 30 giorni scade.
// Riaprendo il modulo (o la scheda) compare: «Hai un … non salvato: Riprendi / Elimina».
// Se il browser non permette l'archivio (navigazione privata, spazio pieno) si continua
// come prima, senza errori.
// ═══════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from "react";

const DB_NOME = "nexiora-bozze";
const STORE = "bozze";
const SCADENZA_MS = 30 * 24 * 3600 * 1000;
const ATTESA_MS = 800;

// Avviso a chi mostra l'elenco delle bozze (es. la scheda Rapportini) che qualcosa è cambiato
const EVENTO = "nexiora:bozze";
function avvisa() { try { window.dispatchEvent(new Event(EVENTO)); } catch { /* */ } }

let apertura = null;
function apriDb() {
  if (apertura) return apertura;
  apertura = new Promise((ok, ko) => {
    try {
      if (typeof indexedDB === "undefined") { ko(new Error("IndexedDB non disponibile")); return; }
      const r = indexedDB.open(DB_NOME, 1);
      r.onupgradeneeded = () => { if (!r.result.objectStoreNames.contains(STORE)) r.result.createObjectStore(STORE, { keyPath: "chiave" }); };
      r.onsuccess = () => ok(r.result);
      r.onerror = () => ko(r.error);
      r.onblocked = () => ko(new Error("archivio bloccato"));
    } catch (e) { ko(e); }
  }).catch(e => { apertura = null; throw e; });
  return apertura;
}

async function operazione(modo, fn) {
  const db = await apriDb();
  return new Promise((ok, ko) => {
    const tx = db.transaction(STORE, modo);
    const r = fn(tx.objectStore(STORE));
    tx.oncomplete = () => ok(r?.result);
    tx.onerror = () => ko(tx.error);
    tx.onabort = () => ko(tx.error);
  });
}

/** Chiave di una bozza: utente | tipo di modulo | commessa | documento (o «nuovo»). */
export const chiaveBozza = (uid, tipo, gruppo, docId) => [uid || "?", tipo, gruppo || "-", docId || "nuovo"].join("|");
/** Inizio comune delle chiavi delle bozze di un tipo di modulo in una commessa. */
export const prefissoBozze = (uid, tipo, gruppo) => [uid || "?", tipo, gruppo || "-", ""].join("|");
/** Il documento a cui si riferisce una bozza («nuovo» per un modulo nuovo). */
export const documentoBozza = (chiave) => String(chiave).split("|")[3] || "nuovo";

/**
 * Toglie dai dati quello che non si può (o non si deve) rimettere in un modulo: valori del
 * database come le date di creazione (si riprendono dall'originale) e funzioni.
 */
export function datiPuliti(v) {
  if (v === null || v === undefined) return v;
  if (Array.isArray(v)) return v.map(datiPuliti);
  if (typeof v === "function") return undefined;
  if (typeof v === "object") {
    if (typeof v.toDate === "function" || typeof v.toMillis === "function" || v instanceof Date) return undefined;
    const o = {};
    Object.keys(v).forEach(k => { const x = datiPuliti(v[k]); if (x !== undefined) o[k] = x; });
    return o;
  }
  return v;
}

export async function salvaBozza(chiave, dati, info = {}) {
  try { await operazione("readwrite", s => s.put({ chiave, dati: datiPuliti(dati), info, salvataIl: Date.now() })); return true; }
  catch { return false; }
}

export async function leggiBozza(chiave) {
  try {
    const b = await operazione("readonly", s => s.get(chiave));
    if (!b) return null;
    if (Date.now() - (b.salvataIl || 0) > SCADENZA_MS) { cancellaBozza(chiave); return null; }
    return b;
  } catch { return null; }
}

export async function cancellaBozza(chiave) {
  try { await operazione("readwrite", s => s.delete(chiave)); } catch { /* niente da fare */ }
  avvisa();
}

/** Le bozze che iniziano con un prefisso (es. tutte quelle dei rapportini di una commessa). */
export async function bozzeCon(prefisso) {
  try {
    const tutte = await operazione("readonly", s => s.getAll());
    const ora = Date.now();
    return (tutte || []).filter(b => b.chiave.startsWith(prefisso) && ora - (b.salvataIl || 0) <= SCADENZA_MS)
      .sort((a, b) => b.salvataIl - a.salvataIl);
  } catch { return []; }
}

/** «17/10 alle 18:05» */
export function quandoBozza(ms) {
  const d = new Date(ms);
  const p = n => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)} alle ${p(d.getHours())}:${p(d.getMinutes())}`;
}


/** Elenco aggiornato delle bozze con un prefisso. */
export function useBozzeCon(prefisso) {
  const [lista, setLista] = useState([]);
  useEffect(() => {
    let vivo = true;
    const carica = () => bozzeCon(prefisso).then(l => { if (vivo) setLista(l); });
    carica();
    window.addEventListener(EVENTO, carica);
    return () => { vivo = false; window.removeEventListener(EVENTO, carica); };
  }, [prefisso]);
  return lista;
}

/**
 * Bozza automatica di un modulo.
 *  chiave: chiaveBozza(...) · valore: lo stato del modulo · info: testo per l'elenco (es. data)
 *  riprendiSubito: true quando il modulo è stato aperto proprio con «Riprendi» dall'elenco.
 * Restituisce:
 *  trovata  — la bozza in attesa di decisione (o null): mostrare AvvisoBozza
 *  riprendi — rimette i dati nel modulo (chiama applica(dati))
 *  scarta   — elimina la bozza e continua con il modulo com'è
 *  salvato  — da chiamare quando il modulo è stato salvato davvero (cancella la bozza)
 *  riattiva — se il salvataggio non è andato a buon fine: la bozza riprende a salvarsi
 *  salvaCon(fn) — per i moduli che salvano con una funzione di chi li apre (onSave): la
 *             bozza si ferma, si salva; se il modulo resta aperto (dati mancanti o errore)
 *             la bozza riprende, se si chiude viene cancellata.
 * info: { titolo, vai } — titolo leggibile e dove si trova il modulo (per «Le mie bozze»).
 */
export function useBozzaModulo({ chiave, valore, info, applica, riprendiSubito = false }) {
  const [trovata, setTrovata] = useState(null);
  const [pronta, setPronta] = useState(false);     // finché non si è letto l'archivio non si scrive
  const iniziale = useRef(JSON.stringify(datiPuliti(valore)));
  // Finché la persona non tocca il modulo, i cambiamenti automatici (es. il nome del
  // cliente che arriva un attimo dopo) non sono «scritti da lei»: non creano bozze.
  const toccato = useRef(false);
  useEffect(() => {
    const segna = () => { toccato.current = true; };
    const eventi = ["input", "change", "pointerdown", "keydown"];
    eventi.forEach(ev => document.addEventListener(ev, segna, true));
    return () => eventi.forEach(ev => document.removeEventListener(ev, segna, true));
  }, []);
  useEffect(() => { if (!toccato.current) iniziale.current = JSON.stringify(datiPuliti(valore)); }, [valore]);
  const chiusa = useRef(false);                     // dopo il salvataggio vero non si scrive più
  const ultimo = useRef(valore);
  ultimo.current = valore;
  const infoRef = useRef(info);
  infoRef.current = info;
  const applicaRef = useRef(applica);
  applicaRef.current = applica;

  // all'apertura: c'è una bozza?
  useEffect(() => {
    let vivo = true;
    leggiBozza(chiave).then(b => {
      if (!vivo) return;
      if (b && JSON.stringify(b.dati) !== iniziale.current) {
        if (riprendiSubito) applicaRef.current?.(b.dati);
        else setTrovata(b);
      }
      setPronta(true);
    });
    return () => { vivo = false; };
  }, [chiave, riprendiSubito]);

  const scrivi = useCallback(() => {
    if (chiusa.current || !toccato.current) return;
    const json = JSON.stringify(datiPuliti(ultimo.current));
    if (json === iniziale.current) return; // nulla di scritto: nessuna bozza
    salvaBozza(chiave, ultimo.current, infoRef.current).then(avvisa);

  }, [chiave]);

  // dopo ogni modifica, con un attimo di pausa (non mentre si aspetta «Riprendi / Elimina»)
  useEffect(() => {
    if (!pronta || trovata) return undefined;
    const t = setTimeout(scrivi, ATTESA_MS);
    return () => clearTimeout(t);
  }, [valore, pronta, trovata, scrivi]);

  // uscendo dall'app (o chiudendo la pagina) si salva subito
  useEffect(() => {
    if (!pronta || trovata) return undefined;
    const subito = () => { if (document.visibilityState === "hidden") scrivi(); };
    window.addEventListener("pagehide", scrivi);
    document.addEventListener("visibilitychange", subito);
    return () => { window.removeEventListener("pagehide", scrivi); document.removeEventListener("visibilitychange", subito); };
  }, [pronta, trovata, scrivi]);

  // chiudendo il modulo senza salvare: l'ultima modifica non deve restare in sospeso
  const statoRef = useRef({ pronta, trovata });
  statoRef.current = { pronta, trovata };
  useEffect(() => () => { if (statoRef.current.pronta && !statoRef.current.trovata) scrivi(); }, [scrivi]);

  const riprendi = () => { if (trovata) applicaRef.current?.(trovata.dati); setTrovata(null); };
  const scarta = () => { cancellaBozza(chiave); setTrovata(null); };
  const salvato = () => { chiusa.current = true; cancellaBozza(chiave); };
  const riattiva = () => { chiusa.current = false; scrivi(); };
  const montato = useRef(true);
  useEffect(() => { montato.current = true; return () => { montato.current = false; }; }, []);
  const salvaCon = async (fn) => {
    chiusa.current = true;
    try {
      const r = await fn();
      setTimeout(() => { if (montato.current) riattiva(); else cancellaBozza(chiave); }, 1500);
      return r;
    } catch (e) {
      if (montato.current) riattiva();
      throw e;
    }
  };
  return { trovata, riprendi, scarta, salvato, riattiva, salvaCon };
}
